import { DestroyRef, EnvironmentInjector, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Unsubscribe } from 'firebase/firestore';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CrwdConfirmService, crwdBelow, crwdFrom, CrwdMenuItem, CrwdNavAction, CrwdNavGroupToggle, CrwdNavItem } from '@crewdle/ui-kit';
import { WhiteLabelService } from '@crewdle/white-label/angular';
import { PartnerDomainsService } from '../partner-domains/partner-domains.service';
import { WebHandoffService } from '../web-handoff/web-handoff.service';

import { AppHeaderService } from 'src/app/services/app-header/app-header.service';
import { GlobalSearchService } from 'src/app/services/global-search/global-search.service';
import { NeedsYouService } from 'src/app/services/needs-you/needs-you.service';
import { MemoryService } from 'src/app/services/memory/memory.service';
import { WhatsNewService } from 'src/app/services/whats-new/whats-new.service';
import { ChatSidebarService, SidebarChatFolderItem, SidebarChatItem } from 'src/app/services/chat-sidebar/chat-sidebar.service';
import { PRODUCT_ID } from 'src/app/config/product';
import { FeatureAccess, ProductAccessService } from 'src/app/services/product-access/product-access.service';
import { ConversationCreateService } from 'src/app/services/conversation-create/conversation-create.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { SidebarAgentItem, SidebarListsService, SidebarSharedAgentItem, SidebarSpaceItem } from 'src/app/services/sidebar-lists/sidebar-lists.service';
import { TeamsService } from 'src/app/services/teams/teams.service';
import { TeamsService as MicrosoftTeamsService } from 'src/app/chat/core/services/teams.service';
import { SidebarTeamItem } from 'src/app/models/team/team.model';
import { UserService } from 'src/app/services/user/user.service';
import { ProfileService, SettingsTabs } from 'src/app/services/profile/profile.service';
import { SupportChatbotService } from 'src/app/components/support-chatbot/support-chatbot.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { agentColorFor } from 'src/app/utils/agent-color';
import { creditsToDollarLabel } from 'src/app/utils/credits';
import { environment } from 'src/environments/environment';
import { LocalAgentUnreadService } from '../local-agents/local-agent-unread.service';
import { UnreadCountsStore } from '../messaging-listener/unread-counts.store';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { NavSeenService, NavSectionCreations } from 'src/app/services/nav-seen/nav-seen.service';
import { ChatFolderHintsRegistry } from 'src/app/services/nav-seen/chat-folder-hints.registry';
import { isNavSection, NAV_SECTIONS, NavSection, NO_SECTION, OpenSection, SECTION_ITEM_PREFIX, sectionForUrl } from './nav-section';
import { adminOrgUrl } from 'src/app/services/org-url/org-url';

/**
 * Every rail section shows this many rows before "Show more", which then
 * reveals a step at a time. The kit windows the rows itself — these only
 * describe the window.
 */
const INITIAL_ROSTER_ROWS = 6;
const ROSTER_STEP = 12;

/** Section entries, in the rail's order (navigation spec §3.1). */
const SECTION_ICONS: Record<NavSection, string> = {
  chats: 'message-circle',
  topics: 'tag',
  agents: 'sparkles',
  teams: 'briefcase-outline',
};

/** Each section's base page (spec §3.3). `/agents` stays the Dashboard ("Waiting for you"). */
/** Pages the sections' create rows open (chats/topics create in place). */
const CREATE_ROUTES: Partial<Record<NavSection, string>> = {
  agents: '/agents/hire',
  teams: '/projects/hire',
};

const SECTION_HOME: Record<NavSection, string | null> = {
  chats: '/chat',
  topics: '/chat/topics',
  agents: '/agents/start',
  teams: '/projects/start',
};

/** `create:<action>` — the open section's create row ("New agent"…), replacing the heading "+". */
const CREATE_ITEM_PREFIX = 'create:';

/** The section of the last page that belonged to one, per org — restored on a page that belongs to none. */
const SECTION_STORAGE_PREFIX = 'crwd.nav.section.';

/** The part of crwd-sidebar the section entries need: re-opening its drawer. */
export interface SidebarDrawerHandle {
  mobileOpen: { set(value: boolean): void };
}

/**
 * Everything connect's rail needs that is NOT chrome.
 *
 * The chrome is `crwd-sidebar`, projected straight into `crwd-app-shell` by
 * AppComponent — there is no rail component any more, because the kit shell
 * finds its sidebar with `contentChild(CrwdSidebar)` and a wrapper would hide
 * it. So the DATA (four live rosters plus the URL's active row) and the
 * ACTIONS live here instead, the same shape admin uses (`nav.navItems()`).
 *
 * `navItems` is a signal rebuilt only when an input changes — never per
 * change-detection pass, which would hand the kit a new array (and new menu
 * arrays) on every tick and re-render any open menu for nothing.
 */
@Injectable({ providedIn: 'root' })
export class SidebarNavService {
  /** What crwd-sidebar renders. Rebuilt by {@link rebuild}, never in a getter. */
  public readonly navItems = signal<CrwdNavItem[]>([]);

  /**
   * The section whose rows the panel shows. Follows the URL on every
   * NavigationEnd (sectionForUrl); a section entry click sets it directly.
   */
  public readonly openSection = signal<OpenSection>('chats');
  /** A section was picked by hand since the last navigation — live lookups must not move it back. */
  private sectionPickedByHand = false;

  private billingSettingsUnsub: Unsubscribe | null = null;
  private billingSettingsOrgId: string | null = null;
  private readonly lowBalanceThreshold = signal<number | null>(null);
  private readonly lowBalanceDismissed = signal(false);

  private readonly productDomain = environment.production ? 'crewdle.ai' : 'stg.crewdle.ai';

  private readonly subscriptions: Subscription[] = [];
  private readonly expandedChatFolders = new Set<string>();
  /** Per-section reveal windows, matching the kit's own Show more stepping. */
  private chatAllowed = false;
  /** Agents / teams as restricted per role in Admin → Tools (2026-09-18); everything on until resolved. */
  private featureAccess: FeatureAccess = { agents: true, teams: true };
  /** The role may use the Connect product (agents, tasks, memory…). True while
   *  access is pending, so the rail never blinks for the usual member. */
  private connectAllowed = true;

  public readonly appHeader = inject(AppHeaderService);
  /** Every card and task waiting on the person, counted once: the Dashboard badge. */
  public readonly needsYou = inject(NeedsYouService);
  private readonly memory = inject(MemoryService);
  private readonly whatsNew = inject(WhatsNewService);
  public readonly chatSidebar = inject(ChatSidebarService);
  private readonly productAccess = inject(ProductAccessService);
  public readonly lists = inject(SidebarListsService);
  public readonly teams = inject(TeamsService);
  public readonly organization = inject(OrganizationService);
  public readonly profile = inject(ProfileService);
  public readonly user = inject(UserService);
  public readonly whiteLabel = inject(WhiteLabelService);
  private readonly partnerDomains = inject(PartnerDomainsService);
  private readonly webHandoff = inject(WebHandoffService);
  private readonly conversationCreate = inject(ConversationCreateService);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly firebase = inject(FirebaseService);
  private readonly globalSearch = inject(GlobalSearchService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly supportChatbot = inject(SupportChatbotService);
  private readonly translate = inject(TranslateService);
  private readonly confirms = inject(CrwdConfirmService);
  private readonly navSeen = inject(NavSeenService);
  private readonly chatFolderHints = inject(ChatFolderHintsRegistry);

  /** Called once by AppComponent after login settles. Idempotent. */
  public async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    // The auth guard has usually settled the Teams detection already; if it lands later, drop the Local models row.
    void this.microsoftTeams.ensureTeamsDetected().then((inTeams) => {
      if (inTeams && this.started) this.rebuild();
    });
    // OrganizationService.data is set imperatively after login finishes.
    // Wait for it to settle, then start the roster listeners. The rosters
    // re-bind themselves on orgId$ (in-place org switch — OrgSwitcherService —
    // or a remote currentOrganizationId change); this service only has to
    // follow the org for its own billing listener and repaint.
    await this.organization.isReady();
    this.lists.start();
    this.teams.start();
    this.chatSidebar.start();
    // The Dashboard badge: the approvals inbox (every open card waiting on the person) + the open tasks that need them.
    this.needsYou.start();
    this.whatsNew.start();
    this.memory.watchReviewBadge();
    this.refreshBillingSettingsSubscription();

    this.followUrl();
    this.subscriptions.push(
      this.organization.orgId$.subscribe(() => {
        this.refreshBillingSettingsSubscription();
        this.expandedChatFolders.clear();
        this.followUrl();
        this.rebuild();
      }),
      this.chatSidebar.allowed$.subscribe((allowed) => {
        this.chatAllowed = allowed === true;
        this.rebuild();
      }),
      // A role without agents (or teams) access loses that whole section and its
      // "+"; the data listeners keep running so re-granting repaints at once.
      this.productAccess.featureAccess$.subscribe((access) => {
        this.featureAccess = access ?? { agents: true, teams: true };
        this.rebuild();
      }),
      // A Chat-only role (Admin → Product access, e.g. Cogir's Regular User):
      // every Connect row went to a guarded page that bounced back to /chat, so
      // clicking Agents/Tasks/Memory "did nothing" (prod 2026-10-28). Hide them.
      this.productAccess.allowedProducts$.subscribe((products) => {
        this.connectAllowed = products === null || products.includes(PRODUCT_ID);
        this.rebuild();
      }),
      this.chatSidebar.roster$.subscribe(() => this.rebuild()),
      // A chat's topic is only known once its folder's chats are listed.
      this.chatSidebar.folderChats$.subscribe(() => {
        this.followUrl({ keepHandPick: true });
        this.rebuild();
      }),
      // …or once the chat page reads it from the chat doc (older than the window, review S5).
      this.chatFolderHints.changes$.subscribe(() => {
        this.followUrl({ keepHandPick: true });
        this.rebuild();
      }),
      this.lists.agents$.subscribe(() => this.rebuild()),
      // The unread dots are per-row state; a change must repaint the rows. A plain
      // synchronous stream, NOT toObservable(ids): that emits on effect timing and
      // left dots set-but-unpainted (see the unread-dot integration spec).
      this.localAgentUnread.changes$.subscribe(() => this.rebuild()),
      // Unread counts (MessagingListenerService → UnreadCountsStore) badge the
      // agent, team and space rows.
      this.unreadCounts.counts$.subscribe(() => this.rebuild()),
      this.lists.spaces$.subscribe(() => this.rebuild()),
      this.lists.sharedAgents$.subscribe(() => this.rebuild()),
      this.teams.sidebarTeams$.subscribe(() => {
        this.followUrl({ keepHandPick: true });
        this.rebuild();
      }),
      // Section badges: marks from the user doc / another device.
      this.navSeen.changes$.subscribe(() => this.rebuild()),
      // Cards and tasks waiting on the person badge the Dashboard row (repaint only when the count moves).
      this.needsYou.count$.subscribe(() => this.rebuild()),
      // Memory items waiting for this person (own notes + admin review) badge the Memory row, one number.
      this.memory.reviewBadge$.subscribe(() => this.rebuild()),
      // What's new: the unread dot (entries this person can see, published since they last opened it).
      this.whatsNew.unread$.subscribe(() => this.rebuild()),
      // Active rows come from the URL, and labels from the active language.
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.followUrl();
        this.rebuild();
      }),
      this.translate.onLangChange.subscribe(() => this.rebuild()),
    );
    this.rebuild();
  }

  private started = false;

  private readonly localAgentUnread = inject(LocalAgentUnreadService);
  private readonly unreadCounts = inject(UnreadCountsStore);
  /** Conversations the rows just built point at — reported so the tab title counts exactly these. */
  private readonly visibleConversationIds = new Set<string>();
  private readonly platform = inject(PlatformService);
  /** Microsoft Teams host detection (not the agent teams service above). */
  private readonly microsoftTeams = inject(MicrosoftTeamsService);
  /** Static from boot: the shell either injected its bridge before Angular or never will. */
  private readonly localModelsAvailable = this.platform.is('electron');
  /** The iOS/Android app (Capacitor). Static from boot, like the bridge check above. */
  public readonly isMobileApp = this.platform.is('capacitor');
  /** Below the width where the sidebar becomes a drawer (1024px): phone and tablet, in the app and on the web alike. */
  private readonly drawerQuery = typeof window.matchMedia === 'function' ? window.matchMedia(crwdBelow('lg')) : null;
  private readonly drawerWidth = signal(this.drawerQuery?.matches ?? false);
  private readonly onDrawerWidthChange = (): void => this.drawerWidth.set(this.drawerQuery?.matches ?? false);
  /** The org tabs live at the top of the drawer, not in a banner row: the mobile app, and the web at phone or tablet width. */
  public get orgTabsInDrawer(): boolean {
    return this.isMobileApp || this.drawerWidth();
  }
  /** The width where the sidebar stops being a drawer (drawerBelow="desktop"): below it the web is in phone or tablet format. */
  private readonly wideQuery = typeof window.matchMedia === 'function' ? window.matchMedia(crwdFrom('lg')) : null;
  private readonly onWidthChange = (): void => {
    if (this.started) this.rebuild();
  };
  private readonly onVisibilityChange = (): void => {
    if (this.started && document.visibilityState === 'visible') this.rebuild();
  };

  /** Below 1024px the rail is a drawer: a section tap switches the panel in place (spec §5). */
  private get isDrawer(): boolean {
    return !(this.wideQuery?.matches ?? true);
  }

  /**
   * The Local models row is for the desktop app (the manager) and the web (the download page). It is not a
   * phone or tablet feature: the mobile app would open a Mac/Windows download page on a phone, and so would the
   * web in a phone or tablet format (below 1024px). The desktop app keeps it at any window size. Never inside
   * Microsoft Teams: its webview can't open the Mac/Windows downloads (store review asked to hide it).
   */
  private get showLocalModelsRow(): boolean {
    if (this.platform.is('capacitor')) return false;
    if (this.microsoftTeams.isTeamsEnvironment()) return false;
    if (this.platform.is('electron')) return true;
    return this.wideQuery?.matches ?? true;
  }

  constructor(
  ) {
    this.wideQuery?.addEventListener('change', this.onWidthChange);
    this.drawerQuery?.addEventListener('change', this.onDrawerWidthChange);
    // Coming back to the tab clears the open section's badge (spec §3.2).
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    inject(DestroyRef).onDestroy(() => {
      this.wideQuery?.removeEventListener('change', this.onWidthChange);
      this.drawerQuery?.removeEventListener('change', this.onDrawerWidthChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.billingSettingsUnsub?.();
      this.billingSettingsUnsub = null;
      this.subscriptions.forEach((s) => s.unsubscribe());
      this.subscriptions.length = 0;
    });
  }

  // ── The nav model ──

  private rebuild(): void {
    this.visibleConversationIds.clear();
    this.markOpenSectionSeen();
    this.navItems.set(this.buildNavItems());
    // The tab-title total counts every row the rail HOLDS, open panel or not —
    // as before the four-section rail, where all agent and team rows were built.
    // Only the open panel's rows register through unreadBadge, so the closed
    // sections' conversations are added here.
    for (const section of this.visibleSections()) {
      for (const id of this.sectionConversationIds(section)) {
        if (id) this.visibleConversationIds.add(id);
      }
    }
    this.unreadCounts.setVisible(new Set(this.visibleConversationIds));
  }

  /** The unread badge for a row (1–9 or '9+'), also registering the row as visible. */
  private unreadBadge(conversationId: string | null | undefined): number | '9+' | undefined {
    if (conversationId) this.visibleConversationIds.add(conversationId);
    return this.unreadCounts.badgeFor(conversationId);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  /** The Show more / Show less window every roster section shares. */
  private window(): Pick<CrwdNavItem, 'maxVisible' | 'showMoreStep' | 'showMoreLabel' | 'showLessLabel'> {
    return {
      maxVisible: INITIAL_ROSTER_ROWS,
      showMoreStep: ROSTER_STEP,
      showMoreLabel: `${this.t('sidebar.show-more')} ({count})`,
      showLessLabel: this.t('sidebar.show-less'),
    };
  }

  /**
   * The rail: the top rows exactly as before the four-section work — Search,
   * Dashboard, Tasks, Marketplace (when agents or teams are allowed) and Local
   * models (product decision 2026-10-20) — then the four section entries
   * (Chats, Topics, Agents, Teams, with their new-item badges), then the OPEN
   * section's heading and rows (navigation spec §3.1, A2: no kit change).
   */
  private buildNavItems(): CrwdNavItem[] {
    const panels: Record<NavSection, () => CrwdNavItem[]> = {
      chats: () => this.chatsPanel(),
      topics: () => this.topicsPanel(),
      agents: () => this.agentSection(),
      teams: () => this.teamSection(),
    };
    const visible = this.visibleSections();
    const requested = this.openSection();
    // NO_SECTION (Dashboard, Tasks, Marketplace): every section closed. A hidden
    // open section falls back to the first visible one for this build only:
    // access resolves asynchronously (Chat product, roles), and storing the
    // fallback would lose the URL's section for good.
    const open: NavSection | null = requested === NO_SECTION
      ? null
      : (visible.includes(requested) ? requested : (visible[0] ?? null));

    const counts = this.navSeen.counts(this.organization.data?.id ?? null, this.sectionCreations());

    const items = this.topRows();
    // Hairlines between the rail's groups — navigation, where you get things
    // (Marketplace, Local models), your work (the sections) — so different
    // kinds of rows stop reading as one list (Vincent, 2026-10-20).
    const getAt = items.findIndex((item) => item.id === 'marketplace' || item.id === 'local-models');
    if (getAt > 0) items.splice(getAt, 0, { id: 'divider:get', label: '', divider: true });
    if (visible.length > 0) items.push({ id: 'divider:sections', label: '', divider: true });
    for (const section of visible) {
      const isOpen = section === open;
      // The entry IS the section's heading (kit `entry`): the open section's
      // rows sit directly under it, windowed by Show more, with no second
      // heading repeating its name (Vincent, 2026-10-20).
      const entry: CrwdNavItem = {
        id: `${SECTION_ITEM_PREFIX}${section}`,
        label: this.sectionTitle(section),
        icon: SECTION_ICONS[section],
        heading: true,
        entry: true,
        active: isOpen,
        badge: NavSeenService.badge(counts[section]),
        // A CLOSED section still says "someone wrote to you" (review S1): its
        // rows' unread counts are not on screen. Unread shows as a count circle
        // (kit `dot` with a number — Vincent, 2026-10-21: "a badge with the
        // number instead of just a dot"), styled like every sidebar number.
        dot: isOpen ? false : this.sectionUnreadCount(section),
      };
      if (!isOpen) {
        items.push(entry);
        continue;
      }
      items.push(...this.openSectionRows(section, entry, panels[section]()));
    }
    return items;
  }

  /**
   * The open section under its entry: the "<Section> home" row (drawer), the
   * create row that replaces the old heading "+" ("New agent"…), then the rows.
   * The panel builders still return [heading, ...rows]; the heading's window
   * moves onto the entry, widened by the lead rows so the roster keeps its size.
   */
  private openSectionRows(section: NavSection, entry: CrwdNavItem, panel: CrwdNavItem[]): CrwdNavItem[] {
    const heading = panel[0]?.heading ? panel[0] : undefined;
    const rows = heading ? panel.slice(1) : panel;
    const lead: CrwdNavItem[] = [];
    const home = this.sectionHome(section);
    if (home && this.isDrawer) {
      lead.push({ id: `section-home:${section}`, label: this.t('sections.home', { section: this.sectionTitle(section) }), icon: 'home' });
    }
    const create = heading?.actions?.[0] ?? heading?.emptyAction;
    // Muted: an action among the roster, not one more team/agent.
    if (create) {
      const createRoute = CREATE_ROUTES[section];
      lead.push({
        id: `${CREATE_ITEM_PREFIX}${create.id}`,
        label: this.createLabel(section),
        icon: 'plus',
        muted: true,
        // On its catalog page the create row is where you are.
        active: !!createRoute && (this.router.url ?? '').split('?')[0] === createRoute,
      });
    }
    const window: Partial<CrwdNavItem> = heading?.maxVisible
      ? {
        maxVisible: heading.maxVisible + lead.length,
        showMoreStep: heading.showMoreStep,
        showMoreLabel: heading.showMoreLabel,
        showLessLabel: heading.showLessLabel,
      }
      : {};
    return [{ ...entry, ...window }, ...lead, ...rows];
  }

  private createLabel(section: NavSection): string {
    switch (section) {
      case 'chats': return this.t('sidebar.new-chat');
      case 'topics': return this.t('sidebar.new-folder');
      case 'agents': return this.t('sections.new-agent');
      case 'teams': return this.t('teams.new-team');
    }
  }

  /**
   * Unread messages across the conversations behind a section's rows (an unseen
   * local agent reply counts one), for the closed section's count circle; the
   * kit shows anything over 9 as "9+". Reads the stores directly instead of
   * building the closed panel. Chats and topics have no unread state.
   */
  private sectionUnreadCount(section: NavSection): number {
    let total = 0;
    for (const id of this.sectionConversationIds(section)) {
      if (!id) continue;
      const badge = this.unreadCounts.badgeFor(id);
      if (badge === '9+') total += 10;
      else if (typeof badge === 'number') total += badge;
      else if (this.localAgentUnread.has(id)) total += 1;
    }
    return total;
  }

  /** The conversations behind a section's rows (agents incl. shared ones, team spaces). */
  private sectionConversationIds(section: NavSection): (string | null | undefined)[] {
    if (section === 'agents') {
      return [
        ...(this.lists.agents$.getValue() ?? []).map((agent) => agent.conversationId),
        ...(this.lists.sharedAgents$.getValue() ?? []).map((item) => item.conversationId),
      ];
    }
    if (section === 'teams') return (this.teams.sidebarTeams$.getValue() ?? []).map((team) => team.conversationId);
    return [];
  }

  /**
   * Being IN a section is seeing it: its badge clears while the tab is visible
   * (spec §3.2). Not for a fallback panel — that may be a transient one while
   * access resolves. Runs before each build, so the build itself stays pure.
   */
  private markOpenSectionSeen(): void {
    const open = this.openSection();
    if (open === NO_SECTION || document.visibilityState === 'hidden' || !this.visibleSections().includes(open)) return;
    const orgId = this.organization.data?.id ?? null;
    const creations = this.sectionCreations();
    const hadBadge = this.navSeen.counts(orgId, creations)[open] > 0;
    this.navSeen.markSeen(orgId, open, creations[open], hadBadge);
  }

  /** The rows above the sections — unchanged from before the four-section rail. */
  private topRows(): CrwdNavItem[] {
    return [
      // The ONE search entry point: chats, agents, teams and messages in a
      // single palette (app-global-search). Cmd/Ctrl+K opens the same thing.
      { id: 'search', label: this.t('search.title'), icon: 'search' },
      ...this.connectRows(),
      // The Marketplace hires agents and teams — pointless for a role allowed neither.
      ...(this.connectAllowed && (this.featureAccess.agents || this.featureAccess.teams)
        ? [{ id: 'marketplace', label: this.t('marketplace.title'), icon: 'store', active: this.isMarketplaceActive } satisfies CrwdNavItem]
        : []),
      // Local models run in the desktop shell only (the Electron shell's models.*
      // channels). The row shows on the desktop app and the web (Vincent, 2026-09-29): on desktop it
      // opens the model manager, on the web the Mac/Windows download buttons — and NOT in the mobile
      // app and on a phone or tablet width, where neither makes sense (showLocalModelsRow).
      // Sits under Marketplace on purpose — it is where you GET models.
      ...(this.showLocalModelsRow
        ? [
            {
              id: 'local-models',
              label: this.t('menu.local_models'),
              active: this.router.url.startsWith('/local-models'),
              icon: 'hardware-chip-outline',
              // The Corsair attribution lives HERE, on the row (kit 0.48.1 caption) —
              // the modal deliberately carries none; one credit, where the feature is.
              caption: this.t('local_models.poweredBy'),
              captionLogo: { maskUrl: 'assets/image/corsair-logo.svg', label: 'Corsair' },
            } satisfies CrwdNavItem,
          ]
        : []),
      // What's new (2026-11-01): Crewdle's product news for this org, with a dot while some are unread.
      {
        id: 'whats-new',
        label: this.t('whats-new.title'),
        icon: 'sparkles',
        active: this.router.url.startsWith('/whats-new'),
        dot: this.whatsNew.unread$.getValue() > 0,
      } satisfies CrwdNavItem,
    ];
  }

  /** Dashboard, Tasks and Memory — Connect pages, absent for a Chat-only role. */
  private connectRows(): CrwdNavItem[] {
    if (!this.connectAllowed) return [];
    return [
      {
        id: 'dashboard',
        label: this.t('sidebar.dashboard'),
        icon: 'layout-grid',
        active: this.isDashboardActive,
        // The one "needs you" count (Vincent, 2026-11-03): every card waiting on the person — questions with no task
        // behind them included — and every open task that needs them, each once. The dashboard lists them.
        badge: this.needsYou.count > 0 ? this.needsYou.count : undefined,
      },
      {
        id: 'tasks',
        label: this.t('tasks.title'),
        icon: 'clipboard-check',
        active: this.isTasksActive,
        // No badge: the tasks that need the person are in the Dashboard's count (a card behind a task counted on both
        // rows); the board still marks each one "Needs you".
      },
      // User + org memory: what agents remember (MEMORY.md) — beside Tasks, in
      // the navigation group above Marketplace.
      {
        id: 'memory',
        label: this.t('memory.title'),
        icon: 'brain',
        active: this.isMemoryActive,
        badge: NavSeenService.badge(this.memory.reviewBadge$.value),
      },
    ];
  }

  private sectionTitle(section: NavSection): string {
    return this.t(`sections.${section}.title`);
  }

  /**
   * Sections this user can reach (spec A6): Chats and Topics need the Chat
   * product; Agents and Teams need the Connect product, then a row or the
   * right to create one. If only one remains it still shows.
   */
  private visibleSections(): NavSection[] {
    return NAV_SECTIONS.filter((section) => {
      switch (section) {
        case 'chats':
        case 'topics':
          return this.chatAllowed;
        case 'agents':
          return this.connectAllowed && (this.featureAccess.agents || this.agentRowCount() > 0);
        case 'teams':
          return this.connectAllowed && (this.featureAccess.teams || (this.teams.sidebarTeams$.getValue() ?? []).length > 0);
      }
    });
  }

  /** Creation times of everything the rail holds, per section — the badge input (0 extra reads). */
  private sectionCreations(): NavSectionCreations {
    const roster = this.chatAllowed ? (this.chatSidebar.roster$.getValue() ?? []) : [];
    const ms = (value: number | undefined): value is number => typeof value === 'number' && value > 0;
    const shared = this.lists.sharedAgents$.getValue() ?? [];
    const agents = this.lists.agents$.getValue() ?? [];
    const ownedAgentIds = new Set(agents.map((agent) => agent.agentId));
    return {
      chats: roster
        .filter((row) => !row.folder && !!row.chat && !this.navSeen.isOwnChat(row.chat.chatId))
        .map((row) => row.chat!.createdAtMs)
        .filter(ms),
      topics: roster.filter((row) => !!row.folder).map((row) => row.folder!.createdAtMs).filter(ms),
      agents: [
        ...agents.map((agent) => agent.createdOnMs),
        ...shared.filter((item) => !ownedAgentIds.has(item.agentId)).map((item) => item.createdOnMs),
      ].filter(ms),
      teams: (this.teams.sidebarTeams$.getValue() ?? []).map((team) => team.createdAtMs).filter(ms),
    };
  }

  /**
   * The section the URL belongs to, or (for a page of none) the last one, kept
   * per org. `keepHandPick`: a data refresh re-resolves the URL (a chat's topic
   * becomes known) but never undoes a section the user just picked.
   */
  private followUrl(options: { keepHandPick?: boolean } = {}): void {
    if (options.keepHandPick && this.sectionPickedByHand) return;
    this.sectionPickedByHand = false;
    const fromUrl = sectionForUrl(this.router.url ?? '', {
      chatFolderId: (chatId) => this.folderIdOfChat(chatId),
      isTeamConversation: (conversationId) =>
        (this.teams.sidebarTeams$.getValue() ?? []).some((team) => team.conversationId === conversationId),
    });
    if (fromUrl === NO_SECTION) {
      // A top-row page closes the sections; not persisted, so a later page of
      // no section still restores the last real one.
      this.openSection.set(NO_SECTION);
      return;
    }
    if (fromUrl) {
      this.openSection.set(fromUrl);
      this.persistSection(fromUrl);
      return;
    }
    const stored = this.storedSection();
    if (stored) this.openSection.set(stored);
  }

  /** The listener window first (live), then what the chat page read from the chat doc. */
  private folderIdOfChat(chatId: string): string | null {
    for (const [folderId, chats] of Object.entries(this.chatSidebar.folderChats$.getValue() ?? {})) {
      if (chats.some((chat) => chat.chatId === chatId)) return folderId;
    }
    return this.chatFolderHints.folderOf(chatId) ?? null;
  }

  private sectionStorageKey(): string | null {
    const orgId = this.organization.data?.id;
    return orgId ? `${SECTION_STORAGE_PREFIX}${orgId}` : null;
  }

  private persistSection(section: NavSection): void {
    const key = this.sectionStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, section);
    } catch {
      // Private mode: the URL still drives the section.
    }
  }

  private storedSection(): NavSection | null {
    const key = this.sectionStorageKey();
    if (!key) return null;
    try {
      const value = localStorage.getItem(key);
      return isNavSection(value) ? value : null;
    } catch {
      return null;
    }
  }

  /**
   * A section entry. On desktop it opens the section's base page (when it has
   * one yet) and its panel. In the drawer it only switches the panel and keeps
   * the drawer open — the user came to pick a row (spec §5). The kit closes the
   * drawer itself right after emitting, so it is re-opened on the next
   * microtask, before anything paints.
   */
  public openSectionEntry(section: NavSection, drawer?: SidebarDrawerHandle): void {
    this.openSection.set(section);
    this.sectionPickedByHand = true;
    this.persistSection(section);
    if (this.isDrawer) {
      if (drawer) queueMicrotask(() => drawer.mobileOpen.set(true));
      this.rebuild();
      return;
    }
    const home = this.sectionHome(section);
    this.rebuild();
    if (home) void this.router.navigate([home]);
  }

  /**
   * A section's base page, or null when this user can't reach it. The Agents and
   * Teams base pages are CREATION pages (start from scratch + marketplace) and
   * their routes are gated by connectFeatureGuard; a role restricted from
   * creating still sees its rows (feature access gates creation only), so the
   * entry just opens the panel instead of bouncing off the guard to /agents.
   */
  private sectionHome(section: NavSection): string | null {
    if (section === 'agents' && !this.featureAccess.agents) return null;
    if (section === 'teams' && !this.featureAccess.teams) return null;
    return SECTION_HOME[section];
  }

  /** The drawer's "<Section> home" row: the base page, and the drawer closes. */
  private openSectionHome(section: NavSection): void {
    const home = this.sectionHome(section);
    if (!home) return;
    this.router.navigate([home]);
    this.closeMobile();
  }

  private agentRowCount(): number {
    const agents = this.lists.agents$.getValue() ?? [];
    const ownedAgentIds = new Set(agents.map((agent) => agent.agentId));
    const foreign = (this.lists.sharedAgents$.getValue() ?? []).filter((item) => !ownedAgentIds.has(item.agentId));
    return agents.length + foreign.length;
  }

  /**
   * TOPICS — the hosted Chat product's personal folders (and folders shared
   * with the user), now their own section. Folders are containers, not
   * conversations: they keep their own heading, "+" and Show-more window
   * (thomas.lapointe / Vincent, 2026-09-22), and stay expandable with their
   * chats. Only the user-facing name changes (folder → topic); ids, routes and
   * data keep "folder".
   */
  private topicsPanel(): CrwdNavItem[] {
    if (!this.chatAllowed) return [];
    const roster = this.chatSidebar.roster$.getValue() ?? [];
    const folders = roster.filter((row) => !!row.folder);
    const heading: CrwdNavItem = {
      id: 'folders',
      label: this.sectionTitle('topics'),
      heading: true,
      ...this.window(),
      // A new folder is named in a prompt dialog, not the kit's inline `input`
      // row: that row REPLACES the label with a bare field (2026-09-15).
      actions: [{ id: 'new-folder', label: this.t('sidebar.new-folder') }],
      emptyAction: { id: 'new-folder', label: this.t('sidebar.new-folder') },
    };
    return [heading, ...folders.map((row) => this.folderRow(row.folder!))];
  }

  /** CHATS — pinned chats first, then recency; chats inside a topic live in Topics (spec A5). */
  private chatsPanel(): CrwdNavItem[] {
    if (!this.chatAllowed) return [];
    const roster = this.chatSidebar.roster$.getValue() ?? [];
    const chats = roster.filter((row) => !row.folder && !!row.chat);
    const heading: CrwdNavItem = {
      id: 'chats',
      label: this.sectionTitle('chats'),
      heading: true,
      ...this.window(),
      actions: [{ id: 'new-chat', label: this.t('sidebar.new-chat') }],
      emptyAction: { id: 'start-chat', label: this.t('sidebar.start-chat') },
    };
    return [heading, ...chats.map((row) => this.chatRow(row.chat!))];
  }

  /** A folder is both a destination and a container — see `navigable`. */
  private folderRow(folder: SidebarChatFolderItem): CrwdNavItem {
    const chats = this.folderChatsFor(folder.folderId);
    return {
      id: `folder:${folder.folderId}`,
      label: folder.name,
      // A shared folder shows the people glyph so it reads as someone else's.
      icon: folder.ownerUid ? 'people-outline' : 'folder',
      iconUrl: folder.iconUrl,
      navigable: true,
      expanded: this.expandedChatFolders.has(folder.folderId),
      emptyLabel: this.t('sidebar.folder-empty'),
      active: this.isChatFolderActive(folder),
      children: chats.map((chat) => this.chatRow(chat)),
    };
  }

  /**
   * A rail row navigates, and nothing else: rename/pin/move/delete
   * live in the open chat's own ⋯ menu (chat-navbar), which is a superset of
   * what the row kebab carried — it adds Download, Share, Copy and Clone. Two
   * menus for one object meant two places to keep in step.
   */
  private chatRow(chat: SidebarChatItem): CrwdNavItem {
    return {
      id: `chat:${chat.chatId}`,
      label: chat.title || this.t('sidebar.new-chat'),
      // A pinned chat must LOOK pinned: the roster already floats pinned chats
      // to the top, but pinning also bumps the chat's recency, so a chat that
      // was already first showed no change at all — "pinning does nothing"
      // (thomas.lapointe, 2026-09-22). The bookmark glyph is the same one the
      // chat's ⋯ menu uses for pin/unpin.
      icon: chat.pinned ? 'heroBookmark' : 'message-circle',
      active: this.isChatActive(chat),
    };
  }

  private agentSection(): CrwdNavItem[] {
    // The Admin → Tools role restriction on agents gates HIRING only (Vincent,
    // 2026-10-14): a restricted member still sees the agents they own or that
    // are shared with them — they just get no hire action.
    const canHire = this.featureAccess.agents;
    const agents = this.lists.agents$.getValue() ?? [];
    const hire: CrwdNavAction = { id: 'hire', label: this.t('messaging.agents.hire-btn') };
    const heading: CrwdNavItem = {
      id: 'agents',
      label: this.sectionTitle('agents'),
      heading: true,
      ...this.window(),
      ...(canHire ? { actions: [hire] } : {}),
      ...(canHire ? { emptyAction: hire } : {}),
    };
    // One plain row per agent conversation the user is in (Vincent,
    // 2026-09-11: no second row, no expandable "Shared space" child). Sharing
    // adds colleagues to the owner's conversation itself, so an invitee's row
    // is the same conversation, tagged Shared. `sharedAgents$` is the LEGACY
    // model (a separate channel thread per shared agent, pre-2026-09-11):
    // such a thread still lights its owner's row / lists top-level for an
    // invitee until it is migrated into the owner's conversation.
    const shared = this.lists.sharedAgents$.getValue() ?? [];
    const sharedByAgent = new Map(shared.map((item) => [item.agentId, item]));
    const ownedAgentIds = new Set(agents.map((agent) => agent.agentId));
    const foreign = shared.filter((item) => !ownedAgentIds.has(item.agentId));
    const rows = [
      ...agents.map((agent) => this.agentRow(agent, sharedByAgent.get(agent.agentId))),
      ...foreign.map((item) => this.sharedAgentRow(item)),
    ];
    // Nothing to show and nothing this role may create: no empty heading.
    if (!canHire && rows.length === 0) return [];
    return [heading, ...rows];
  }

  private agentRow(agent: SidebarAgentItem, shared?: SidebarSharedAgentItem): CrwdNavItem {
    return {
      id: `agent:${agent.conversationId}`,
      label: agent.name,
      avatarName: agent.name || '?',
      avatarColor: agentColorFor(agent.name),
      // "Needs maintenance" wins the badge slot (its notice is the only new
      // message it will get until it is back); then new messages; the
      // rented/shared tag shows when caught up.
      badge: (agent.maintenance ? this.t('messaging.agents.maintenance-badge') : undefined) ?? this.unreadBadge(agent.conversationId) ?? (agent.rented
        ? this.t('messaging.agents.rented-badge')
        // Someone else's agent, shared with this user.
        : (agent.ownerId && agent.ownerId !== this.user.user?.uid ? this.t('messaging.agents.shared-badge') : undefined)),
      // An unseen LOCAL reply (kit 0.47.0). Cleared by the read-marker when the
      // conversation is actually viewed — see LocalAgentUnreadService.
      dot: this.localAgentUnread.has(agent.conversationId),
      // Either of the agent's threads counts: the private DM this row opens, or
      // the shared space the owner reached from the DM's header.
      active: this.isAgentActive(agent) || (!!shared && this.isSharedThreadActive(shared)),
    };
  }

  /** A thread shared WITH the user (an agent someone else owns and shared) —
   *  routes into /agents like an owned agent, tagged "Shared". */
  private sharedAgentRow(item: SidebarSharedAgentItem): CrwdNavItem {
    return {
      id: `agent:${item.conversationId}`,
      label: item.name,
      avatarName: item.name || '?',
      avatarColor: agentColorFor(item.name),
      badge: this.unreadBadge(item.conversationId) ?? this.t('messaging.agents.shared-badge'),
      active: this.isSharedThreadActive(item),
    };
  }

  private isSharedThreadActive(item: SidebarSharedAgentItem): boolean {
    return this.router.url.startsWith('/agents') && this.activeConversationId === item.conversationId;
  }

  private teamSection(): CrwdNavItem[] {
    // Same rule as agents: the role restriction gates CREATING a team only.
    const canCreate = this.featureAccess.teams;
    const rows = this.sortedTeams(this.teams.sidebarTeams$.getValue() ?? []);
    if (!canCreate && rows.length === 0) return [];
    const create: CrwdNavAction = { id: 'create-team', label: this.t('teams.new-team') };
    const heading: CrwdNavItem = {
      id: 'teams',
      label: this.sectionTitle('teams'),
      heading: true,
      ...this.window(),
      ...(canCreate ? { actions: [create] } : {}),
      ...(canCreate ? { emptyAction: create } : {}),
    };
    return [heading, ...rows.map((team) => this.teamRow(team))];
  }

  private teamRow(team: SidebarTeamItem): CrwdNavItem {
    return {
      id: `team:${team.key}`,
      label: team.name,
      avatarName: team.name || '?',
      // Seeded on the MANAGER so a team reads as its manager's colour.
      avatarColor: agentColorFor(team.managerName || team.name),
      // Unread messages in the team space (was the member count, which read as
      // "new messages" — Vincent, 2026-09-23). Members show in the space header.
      badge: this.unreadBadge(team.conversationId),
      active: this.isTeamActive(team),
    };
  }

  private spaceSection(): CrwdNavItem[] {
    const spaces = this.lists.spaces$.getValue() ?? [];
    const heading: CrwdNavItem = {
      id: 'spaces',
      label: this.t('sidebar.spaces'),
      heading: true,
      ...this.window(),
      actions: [{
        id: 'create',
        label: this.t('sidebar.new-space'),
        menu: [
          { id: 'new-channel', label: this.t('messaging.channels.create-btn'), icon: 'plus' },
          { id: 'browse-channels', label: this.t('messaging.channels.browse'), icon: 'search' },
        ],
      }],
      emptyAction: { id: 'new-channel', label: this.t('sidebar.new-space') },
    };
    return [heading, ...spaces.map((space) => this.spaceRow(space))];
  }

  private spaceRow(space: SidebarSpaceItem): CrwdNavItem {
    return {
      id: `space:${space.conversationId}`,
      label: space.name,
      glyph: space.channelType === 'private' ? '🔒' : '#',
      badge: this.unreadBadge(space.conversationId),
      active: this.isSpaceActive(space),
    };
  }

  /**
   * TEAMS rows recency-sorted: the team conversation's last activity (from
   * the live agents/channels listeners), falling back to a connect team's
   * creation time when its space has seen no activity within the listener
   * windows. Agents and spaces need no equivalent — their lists arrive
   * `updatedOn desc` from Firestore.
   */
  private sortedTeams(rows: SidebarTeamItem[]): SidebarTeamItem[] {
    const activity = this.lists.conversationActivity$.getValue();
    const lastUsed = (row: SidebarTeamItem) =>
      Math.max(activity[row.conversationId] ?? 0, row.createdAtMs ?? 0);
    return [...rows].sort((a, b) => lastUsed(b) - lastUsed(a));
  }

  // ── What the sidebar emits ──

  public onNavItem(item: CrwdNavItem, drawer?: SidebarDrawerHandle): void {
    const id = item.id ?? '';
    if (id.startsWith(SECTION_ITEM_PREFIX)) {
      const section = id.slice(SECTION_ITEM_PREFIX.length);
      return isNavSection(section) ? this.openSectionEntry(section, drawer) : undefined;
    }
    if (id.startsWith(CREATE_ITEM_PREFIX)) {
      const action = id.slice(CREATE_ITEM_PREFIX.length);
      return this.onSectionAction({ section: item, action: { id: action, label: item.label } });
    }
    if (id.startsWith('section-home:')) {
      const section = id.slice('section-home:'.length);
      return isNavSection(section) ? this.openSectionHome(section) : undefined;
    }
    if (id === 'search') return this.openSearch();
    if (id === 'dashboard') return this.openDashboard();
    if (id === 'tasks') return this.openTasks();
    if (id === 'memory') return this.openMemory();
    if (id === 'marketplace') return this.openMarketplace();
    if (id === 'local-models') return void this.openLocalModels();
    if (id === 'whats-new') {
      this.router.navigate(['/whats-new']);
      return this.closeMobile();
    }
    if (id.startsWith('folder:')) return this.openChatFolder(id.slice('folder:'.length));
    if (id.startsWith('chat:')) return this.openChat(id.slice('chat:'.length));
    if (id.startsWith('agent:')) return this.openConversation('/agents', id.slice('agent:'.length));
    if (id.startsWith('space:')) return this.openConversation('/messaging', id.slice('space:'.length));
    if (id.startsWith('team:')) return this.openTeam(id.slice('team:'.length));
  }

  /**
   * The Local models dialog. A dynamic `import()` on purpose: it pulls the
   * local-inference services and the HuggingFace client, none of which belong in the
   * initial bundle for a row most sessions never touch (and which exists only on
   * desktop). The rail's Local models row (back at the top, 2026-10-20).
   */
  private async openLocalModels(): Promise<void> {
    if (!this.localModelsAvailable) {
      // Web: the download page, in the content pane.
      this.router.navigate(['/local-models']);
      this.closeMobile();
      return;
    }
    const { LocalModelsModalComponent } = await import(
      'src/app/modals/local-models-modal/local-models-modal.component'
    );
    const modal = await this.modalService.create({
      component: LocalModelsModalComponent,
      cssClass: 'local-models-modal',
    });
    await modal.present();
  }

  public onSectionAction({ action }: { section: CrwdNavItem; action: CrwdNavAction }): void {
    switch (action.id) {
      case 'start-chat':
      case 'new-chat': return this.newChat();
      case 'new-folder': return void this.promptNewChatFolder();
      case 'hire': return this.hireAgent();
      case 'create-team': return this.newTeam();
      case 'new-channel': return this.newChannel();
      // The SPACES "+" carries a menu; crwd-sidebar opens it itself.
      default: return;
    }
  }

  public onSectionMenu({ menuItem }: { menuItem: CrwdMenuItem }): void {
    switch (menuItem.id) {
      case 'new-channel': return this.newChannel();
      case 'browse-channels': return this.browseChannels();
      default: return;
    }
  }

  public onGroupToggled({ item, expanded }: CrwdNavGroupToggle): void {
    const folderId = (item.id ?? '').slice('folder:'.length);
    if (!folderId) return;
    if (!expanded) {
      this.expandedChatFolders.delete(folderId);
      this.rebuild();
      return;
    }
    this.expandedChatFolders.add(folderId);
    // The passive roster listener only windows recent chats — fetch the
    // folder's full list on expand.
    void this.chatSidebar.loadFolderChats(folderId).then(() => this.rebuild());
    this.rebuild();
  }

  // ── Chats (hosted Chat product surface) navigation ──

  public isChatActive(chat: SidebarChatItem): boolean {
    return this.router.url.split('?')[0] === `/chat/c/${chat.chatId}`;
  }

  public isChatFolderActive(folder: SidebarChatFolderItem): boolean {
    return this.router.url.split('?')[0] === `/chat/topics/${folder.folderId}`;
  }

  public newChat(): void {
    // /chat is the new-chat composer (chat's home page).
    this.router.navigate(['/chat']);
    this.closeMobile();
  }

  /** Opens the global search palette (hosted in the shell). */
  public openSearch(): void {
    this.closeMobile();
    this.globalSearch.openPalette();
  }

  // ── Entry points the palette shares with the rail rows ──

  public openAgentConversation(conversationId: string): void {
    this.openConversation('/agents', conversationId);
  }

  public openSpaceConversation(conversationId: string): void {
    this.openConversation('/messaging', conversationId);
  }

  public openTeamByKey(key: string): void {
    this.openTeam(key);
  }

  public creatingFolder = false;

  /** Asks for the topic name in a prompt dialog — same shape as renaming a
   * chat — then creates it. Also the Topics base page's "New topic". */
  public async promptNewChatFolder(): Promise<void> {
    const values = await this.confirms.prompt({
      title: this.t('sidebar.new-folder'),
      inputs: [{
        name: 'name',
        type: 'text',
        label: this.t('sidebar.folder-name-label'),
      }],
      confirmLabel: this.t('common.buttons.create'),
      cancelLabel: this.t('common.buttons.cancel'),
    });
    await this.createChatFolder(values?.name ?? '');
  }

  /** Lazy-loads the chat surface's FolderService so the chat graph stays out
   * of the initial bundle (the rail is eagerly loaded). */
  private async createChatFolder(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed || this.creatingFolder) return;
    this.creatingFolder = true;
    try {
      const folderId = await (await this.folderService()).createFolder(trimmed);
      this.closeMobile();
      this.router.navigate(['/chat/topics', folderId]);
    } catch (err) {
      console.error('[SidebarNav] Could not create chat folder', err);
    } finally {
      this.creatingFolder = false;
      this.rebuild();
    }
  }

  /** A folder's chats, pinned ones first (each group keeps its recency order). */
  private folderChatsFor(folderId: string): SidebarChatItem[] {
    const chats = this.chatSidebar.folderChats$.getValue()[folderId] ?? [];
    return [...chats.filter((c) => c.pinned), ...chats.filter((c) => !c.pinned)];
  }

  public openChat(chatId: string): void {
    this.router.navigate(['/chat/c', chatId]);
    this.closeMobile();
  }

  public openChatFolder(folderId: string): void {
    // A folder shared with this user lives under its owner: the page needs the
    // owner to load it (`?owner=`), the path stays the same for active state.
    const row = (this.chatSidebar.roster$.getValue() ?? []).find((r) => r.kind === 'folder' && r.folder?.folderId === folderId);
    const ownerUid = row?.folder?.ownerUid;
    this.router.navigate(['/chat/topics', folderId], ownerUid ? { queryParams: { owner: ownerUid } } : undefined);
    this.closeMobile();
  }

  /** Lazy FolderService seam, with chat's context guaranteed. */
  private async folderService() {
    await this.ensureChatContext();
    const { FolderService } = await import('src/app/chat/core/services/folder.service');
    return this.envInjector.get(FolderService);
  }

  /**
   * Chat's services read the signed-in user and active org from chat's OWN
   * OrgContextService, which only boots on /chat/** (chatContextGuard). The
   * rail offers chat actions from every route, so boot it on demand — init()
   * is idempotent, exactly as the guard relies on.
   */
  private async ensureChatContext(): Promise<void> {
    const { OrgContextService } = await import('src/app/chat/core/services/org-context.service');
    const context = this.envInjector.get(OrgContextService);
    if (!context.currentOrg()) {
      await context.init();
    }
  }

  // ── Agents / Teams / Spaces navigation ──

  /** The conversation currently open (either tab), from the URL. */
  public get activeConversationId(): string | null {
    const params = this.router.routerState.snapshot.root.queryParams;
    return params.conversationId || params.select || null;
  }

  public get isDashboardActive(): boolean {
    return this.router.url.split('?')[0] === '/agents' && !this.activeConversationId;
  }

  public get isTasksActive(): boolean {
    return this.router.url.startsWith('/tasks');
  }

  public get isMemoryActive(): boolean {
    return this.router.url.startsWith('/memory');
  }

  public openMemory(): void {
    // An admin with organization items waiting lands on To review; otherwise their own notes (and their findings).
    this.router.navigate(['/memory'], this.memory.orgReviewWaiting > 0 ? { queryParams: { tab: 'review' } } : {});
    this.closeMobile();
  }

  public get isMarketplaceActive(): boolean {
    return this.router.url.startsWith('/marketplace');
  }

  /** The full marketplace (agents and teams); the Agents / Teams base pages embed a filtered one. */
  public openMarketplace(): void {
    this.router.navigate(['/marketplace']);
    this.closeMobile();
  }

  public openTasks(): void {
    this.router.navigate(['/tasks']);
    this.closeMobile();
  }

  public isAgentActive(agent: SidebarAgentItem): boolean {
    return this.router.url.startsWith('/agents') && this.activeConversationId === agent.conversationId;
  }

  public isSpaceActive(space: SidebarSpaceItem): boolean {
    return this.router.url.startsWith('/messaging') && this.activeConversationId === space.conversationId;
  }

  public openDashboard(): void {
    this.router.navigate(['/agents']);
    this.closeMobile();
  }

  private openConversation(route: string, conversationId: string): void {
    this.router.navigate([route], { queryParams: { conversationId } });
    this.closeMobile();
  }

  /** "New agent": the catalog view — start from scratch or a ready-made agent. */
  public hireAgent(): void {
    this.router.navigate(['/agents/hire']);
    this.closeMobile();
  }

  /** "New team": the catalog view — assemble from scratch or a ready-made team. */
  public newTeam(): void {
    this.router.navigate(['/projects/hire']);
    this.closeMobile();
  }

  public isTeamActive(team: SidebarTeamItem): boolean {
    return this.activeConversationId === team.conversationId;
  }

  private openTeam(key: string): void {
    const team = (this.teams.sidebarTeams$.getValue() ?? []).find((row) => row.key === key);
    if (!team) return;
    this.openConversation('/messaging', team.conversationId);
  }

  /** Assemble a team from scratch: the `/teams/new` page (it replaced the create-team modal). */
  public createTeam(): void {
    this.router.navigate(['/projects/new']);
    this.closeMobile();
  }

  public newChannel(): void {
    this.closeMobile();
    void this.conversationCreate.createChannel();
  }

  public browseChannels(): void {
    this.closeMobile();
    const channelIds = (this.lists.spaces$.getValue() || []).map((s) => s.conversationId);
    void this.conversationCreate.browseChannels(channelIds);
  }

  // ── Balance, account and org chrome ──

  private refreshBillingSettingsSubscription(): void {
    const orgId = this.organization.data?.id ?? null;
    if (orgId === this.billingSettingsOrgId) return;
    this.billingSettingsUnsub?.();
    this.billingSettingsUnsub = null;
    this.billingSettingsOrgId = orgId;
    this.lowBalanceThreshold.set(null);
    this.lowBalanceDismissed.set(false);
    if (!orgId) return;

    this.billingSettingsUnsub = this.firebase.firestore.docListen(
      `organizations/${orgId}/billing/settings`,
      (snap) => {
        const data: any = snap.exists() ? snap.data() : null;
        const value = Number(data?.lowBalanceThresholdCredits ?? 0);
        this.lowBalanceThreshold.set(Number.isFinite(value) && value > 0 ? value : null);
      },
      () => {
        this.lowBalanceThreshold.set(null);
      },
    );
  }

  /** The low-balance nudge now renders as the shell's banner (crwd-banner). */
  get lowBalanceVisible(): boolean {
    const orgId = this.organization.data?.id;
    const threshold = this.lowBalanceThreshold();
    if (!orgId || threshold == null || this.lowBalanceDismissed()) return false;
    if ((this.organization.data?.creditBalance ?? 0) > threshold) return false;
    return sessionStorage.getItem(this.lowBalanceSessionKey(orgId)) !== '1';
  }

  get lowBalanceMessage(): string {
    const balance = creditsToDollarLabel(this.organization.data?.creditBalance ?? 0);
    const threshold = creditsToDollarLabel(this.lowBalanceThreshold() ?? 0);
    const message = this.t('sidebar.low-credits-message', { balance, threshold });
    // Runway at the observed pace — only when the estimate is meaningful
    // (declining balance, under a day left), so the banner stays calm otherwise.
    const minutes = this.organization.runwayMinutes();
    if (minutes == null || minutes > 24 * 60) return message;
    const runway = minutes >= 90
      ? this.t('sidebar.low-credits-runway-hours', { hours: Math.round(minutes / 60) })
      : this.t('sidebar.low-credits-runway', { minutes });
    return `${message} ${runway}`;
  }

  /**
   * The admin console to link out to — Manage organization, Buy more and the
   * low-balance top-up all go through here.
   *
   * On a white-label org this MUST be the partner's own admin domain. It was a
   * bare `admin.{productDomain}`, so a partner user managing their org was sent
   * to Crewdle's console (reported 2026-09-04). `whiteLabel.domainMap()` is the
   * partner's ACTIVE hostnames keyed by catalog product id — exactly what it
   * exists for ("so product switchers link inside the partner's domain set").
   *
   * Read the map rather than deriving `admin.{partnerBase}`: partners choose
   * their own subdomains, and they do NOT always match the product id (V3 AI
   * serves product `ai-studio` from `forge.ai.v3stent.com`), so the pattern is
   * a guess and the map is the truth. Empty on Crewdle-default orgs, and
   * populated only once the org resolves, hence the fallback.
   */
  get adminUrl(): string {
    return this.partnerDomains.origin('admin', `https://admin.${this.productDomain}`);
  }

  dismissLowBalance(): void {
    const orgId = this.organization.data?.id;
    if (orgId) sessionStorage.setItem(this.lowBalanceSessionKey(orgId), '1');
    this.lowBalanceDismissed.set(true);
  }

  /** Top-ups live in the admin console, same as Manage organization. */
  openBilling(): void {
    void this.openBillingPage();
  }

  /**
   * Billing, through the web handoff on mobile so the browser arrives already signed in. The
   * handoff lands on the console HOME, not straight on /billing: a link someone else made
   * should never drop a user directly on a payment page (they see their own account first).
   * If no handoff can be made, the browser opens /billing and asks for sign-in as before.
   */
  private openBillingPage(): Promise<void> {
    const orgId = this.organization.data?.id;
    return this.webHandoff.open(adminOrgUrl(this.adminUrl, orgId), adminOrgUrl(this.adminUrl, orgId, '/billing'), 'redirect.opening-billing');
  }

  private lowBalanceSessionKey(orgId: string): string {
    return `low-balance-toast:${orgId}`;
  }

  openSupport(): void {
    this.supportChatbot.openPanel();
  }

  get userName(): string {
    return this.user.data?.name || this.user.user?.displayName || '';
  }

  /**
   * More than one organization to choose from. The org tabs show for everyone
   * (Vincent, 2026-10-30); with one org their "+" opens a new tab instead of
   * the org picker. Balance and Manage organization live in the user menu.
   */
  get multiOrg(): boolean {
    return (this.user.data?.organizationIds?.length ?? 0) > 1;
  }

  /**
   * Buy more and Manage organization are admin actions — both open the admin
   * console, which a plain member cannot use for either. Admin = the member
   * doc's admin/owner role, or the org's owner.
   */
  get isOrgAdmin(): boolean {
    const org = this.organization.data;
    return this.organization.isAdmin === true || (!!org?.ownerId && org.ownerId === this.user.user?.uid);
  }

  /** Top-ups live in the admin console; connect links out the same way it does for Manage organization. */
  buyMore(): void {
    this.closeMobile();
    void this.openBillingPage();
  }

  /** Opens admin IN the org this tab is in (`/o/{orgId}`); mobile goes through the web handoff (WebHandoffService). */
  manageOrganization(): void {
    this.closeMobile();
    void this.webHandoff.open(adminOrgUrl(this.adminUrl, this.organization.data?.id));
  }

  goToAccount(): void {
    this.closeMobile();
    this.profile.navigateToProfile(SettingsTabs.you);
  }

  goToSettings(): void {
    this.closeMobile();
    this.profile.navigateToProfile();
  }

  logout(): void {
    this.closeMobile();
    this.user.logout();
  }

  /** The drawer's open state is AppHeaderService's — crwd-sidebar two-way binds it. */
  closeMobile(): void {
    this.appHeader.mobileMenuOpen = false;
  }
}
